import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { TransactionStats } from "@/components/transactions/TransactionStats";
import { TransactionFilters } from "@/components/transactions/TransactionFilters";
import { TransactionTableSkeleton } from "@/components/transactions/TransactionTableSkeleton";
import { TransactionDetailsModal } from "@/components/transactions/TransactionDetailsModal";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  Check,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  ChevronsLeft,
  ChevronLeft,
  ChevronsRight,
  CalendarDays,
} from "lucide-react";
import { MerchantApi } from "@/api/merchant.api";
import { ProgramApi } from "@/api/program.api";
import { TransactionApi } from "@/api/transaction.api";
import {
  downloadTransactionsCSV,
  downloadTransactionsPDF,
  TransactionExportData,
} from "@/utils/pdfGenerator";
import { downloadResponseFile } from "@/utils/common.utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/common/PaginationControl";
import { TransferApi } from "@/api/transfer.api";
import { TransferTransactionFilters } from "@/components/transactions/TransferTransactionFilters";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import type { UserInfo } from "@/hooks/use-user-info";
import { ROLE_CODES } from "@/constants";
import { isLoyyalAdmin } from "@/utils/role.utils";

// Define a type for the transactions displayed in the DataTable
interface TransferTransactionDisplay {
  dateTime: string;
  transferPartnerMemberId: string;
  programMemberId: string;
  transactionId: string;
  transferPartnerTransactionId: string;
  partnerName: string;
  pointsRedeemed: number;
  redemptionAmount: number;
  redemptionCurrency: string;
  pointsEarned: number;
  accruedAmount: number;
  accruedCurrency: string;
  program: string;
  status: string;
  _original?: any;
}

const TransferTransactions = () => {
  const userInfo: UserInfo | null = (() => {
    try {
      const stored = sessionStorage.getItem("userInfo");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();
  const loyyalAdmin = isLoyyalAdmin(userInfo?.roleCode);
  const isProgramAdmin = userInfo?.roleCode === ROLE_CODES.PROGRAM_ADMIN;

  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<any>({});
  const [dateRange, setDateRange] = useState("30days");
  const [sortBy, setSortBy] = useState({
    field: "transactionDate",
    direction: "desc",
  });
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransferTransactionDisplay | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [merchants, setMerchants] = useState([]); // These might need to be transfer partners
  const [programs, setPrograms] = useState([]); // These might need to be from/to programs
  const [stats, setStats] = useState({
    totalNumberOfTransactions: 0,
    totalAmountOfTransactions: 0,
    totalPointsPending: 0,
    totalPointsEarned: 0,
    totalPointsRedeemed: 0,
    avgPointsPerTransaction: 0,
    currencyVO: null,
  });
  const [transactions, setTransactions] = useState<
    TransferTransactionDisplay[]
  >([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [transferPartners, setTransferPartners] = useState([]);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(
    undefined,
  );
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // Add debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Extract unique program names from transactions for the filter dropdown
  const availablePrograms = React.useMemo(() => {
    return Array.from(
      new Set(transactions.map((tx: any) => tx.fromProgram || "")),
    );
  }, [transactions]);

  const totalPages = Math.ceil(totalTransactions / rowsPerPage);

  const resolveProgramId = useCallback(
    (filterPayload: { programCode?: string; programId?: number | string }) => {
      if (filterPayload.programId) {
        return Number(filterPayload.programId);
      }
      if (filterPayload.programCode) {
        const program = programs.find(
          (p: any) => p.programCode === filterPayload.programCode,
        );
        return program?.id ? Number(program.id) : null;
      }
      return null;
    },
    [programs],
  );

  const handleProgramChange = (programCode: string) => {
    setFilters((prev) => ({ ...prev, programCode }));
    setCurrentPage(0);
  };

  // Place fetchTransactionsWithFilters at the top-level of the component, before useEffect and handlers
  const fetchTransactionsWithFilters = async (filterPayload: any) => {
    const programId = resolveProgramId(filterPayload);
    if (!programId) {
      setTransactions([]);
      setTotalTransactions(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      let transferPartnerId = null;
      if (filterPayload.transferPartnerId) {
        const partner = transferPartners.find(
          (tp: any) =>
            String(tp.id) === String(filterPayload.transferPartnerId),
        );
        transferPartnerId = partner ? partner.id : null;
      }
      const requestPayload: any = {
        startDateInMillis: filterPayload.startDateInMillis || "",
        endDateInMillis: filterPayload.endDateInMillis || "",
        sortBy: sortBy.field || "transactionDate",
        sortOrder: (sortBy.direction as "asc" | "desc") || "desc",
        pageNumber: filterPayload.pageNumber ?? currentPage,
        pageSize: rowsPerPage,
        transferStatuses: Array.isArray(filterPayload.transferStatuses)
          ? filterPayload.transferStatuses
          : filterPayload.status
            ? [filterPayload.status]
            : [],
        transferPartnerId: transferPartnerId || "",
        programId,
        id: debouncedSearchTerm.trim() || undefined,
        transferPartnerTransactionId:
          filterPayload.transferPartnerTransactionId?.trim() || undefined,
      };
      const txRes =
        await TransferApi.getTransferTransactionsDashboardFromDB(
          requestPayload,
        );
      const transactionsData = txRes.data?.transferTransactionsDataVO;
      const transactionRows = Array.isArray(
        transactionsData?.transferTransactionVOs,
      )
        ? transactionsData.transferTransactionVOs
        : [];
      const dashboardStats = txRes.data?.transferTransactionStatsVOS;
      const mappedTransactions: TransferTransactionDisplay[] = Array.isArray(
        transactionRows,
      )
        ? transactionRows.map((tx) => ({
            dateTime: tx.transactionDate
              ? tx.transactionDate.split("T")[0]
              : "",
            transferPartnerMemberId: tx.transferPartnerMemberId || "",
            programMemberId: tx.programMemberId || "",
            transactionId: tx.id || tx.id.toString(),
            transferPartnerTransactionId: tx.transferPartnerTransactionId || "",
            partnerName: tx.transferPartnerVO?.partnerName || "",
            pointsRedeemed: tx.transferPartnerPoints || 0,
            redemptionAmount: tx.transferPartnerPointsAmount || 0,
            redemptionCurrency: tx.programCurrencyVO?.iso3CurrencyCode || "",
            pointsEarned: tx.programPoints || 0,
            accruedAmount: tx.programPointsAmount || 0,
            accruedCurrency: tx.programCurrencyVO?.iso3CurrencyCode || "",
            program: tx.programVO?.programName || "",
            status: (tx.transferStatus || "PENDING").toLowerCase(),
            _original: tx,
          }))
        : [];
      setTransactions(mappedTransactions);
      setTotalTransactions(
        Number(transactionsData?.totalNumberOfTransactions) ||
          mappedTransactions.length,
      );
      if (dashboardStats) {
        setStats({
          totalNumberOfTransactions:
            dashboardStats.totalNumberOfTransactions || 0,
          totalAmountOfTransactions: dashboardStats.totalAmountOfTransactions || 0,
          totalPointsPending: 0,
          totalPointsEarned: dashboardStats.totalPointsEarned || 0,
          totalPointsRedeemed: dashboardStats.totalPointsRedeemed || 0,
          avgPointsPerTransaction: dashboardStats.avgPointsPerTransaction || 0,
          currencyVO: dashboardStats.currencyVO || null,
        });
      }
    } catch (e) {
      setTransactions([]);
      setTotalTransactions(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch initial data (merchants, programs, stats) - these might need adjustment for transfer
    const fetchData = async () => {
      try {
        // Fetch transfer transaction statistics
        const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
        const statsResponse = await fetch(
          `${API_BASE}/transfer/getTransferTransactionStatisticsFromDB`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({}),
          },
        );

        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats({
            totalNumberOfTransactions: statsData.totalNumberOfTransactions || 0,
            totalAmountOfTransactions: statsData.totalAmountOfTransactions || 0,
            totalPointsPending: 0, // Not provided in API response
            totalPointsEarned: statsData.totalPointsEarned || 0,
            totalPointsRedeemed: statsData.totalPointsRedeemed || 0,
            avgPointsPerTransaction: statsData.avgPointsPerTransaction || 0,
            currencyVO: statsData.currencyVO || null,
          });
        } else {
          console.error("Failed to fetch transfer transaction statistics");
          setStats({
            totalNumberOfTransactions: 0,
            totalAmountOfTransactions: 0,
            totalPointsPending: 0,
            totalPointsEarned: 0,
            totalPointsRedeemed: 0,
            avgPointsPerTransaction: 0,
            currencyVO: null,
          });
        }

        // Placeholder for transfer partners (programs are fetched separately)
        setMerchants([]);
      } catch (e) {
        console.error("Error fetching initial data:", e);
        setStats({
          totalNumberOfTransactions: 0,
          totalAmountOfTransactions: 0,
          totalPointsPending: 0,
          totalPointsEarned: 0,
          totalPointsRedeemed: 0,
          avgPointsPerTransaction: 0,
          currencyVO: null,
        });
      }
    };
    fetchData();
  }, []);

  // Fetch transactions for current page/size
  useEffect(() => {
    fetchTransactionsWithFilters({ ...filters, pageNumber: currentPage });
    // eslint-disable-next-line
  }, [currentPage, rowsPerPage, debouncedSearchTerm, filters, sortBy]);

  // Fetch transfer partners for filter dropdown
  useEffect(() => {
    const fetchTransferPartners = async () => {
      try {
        const res = await TransferApi.getTransferPartnersFromDB({
          pageNumber: 0,
          pageSize: 100,
          sortOrder: "asc",
          sortBy: "partnerName",
        });
        setTransferPartners(
          Array.isArray(res.data?.transferPartnerVOList)
            ? res.data.transferPartnerVOList.map((tp: any) => ({
                id: tp.id,
                name: tp.partnerName,
              }))
            : [],
        );
      } catch (e) {
        setTransferPartners([]);
      }
    };
    fetchTransferPartners();
  }, []);

  // Fetch programs for filter dropdown
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await ProgramApi.getProgramsFromDB({
          pageNumber: 0,
          pageSize: 100,
          sortOrder: "asc",
          sortBy: "programName",
          programCode: null,
          programName: null,
          programType: "TRANSFER",
        });
        let programsData = [];
        if (Array.isArray(res.data?.programVOs)) {
          programsData = res.data.programVOs;
        } else if (Array.isArray(res.data)) {
          programsData = res.data;
        } else if (Array.isArray((res as any)?.programVOs)) {
          programsData = (res as any).programVOs;
        }

        setPrograms(programsData);
      } catch (e) {
        console.error("Error fetching programs:", e);
        setPrograms([]);
      }
    };
    fetchPrograms();
  }, []);

  // Auto-select the first program for the top dropdown
  useEffect(() => {
    if (programs.length > 0 && !filters.programCode) {
      const firstProgram = programs[0] as { programCode?: string };
      if (firstProgram?.programCode) {
        setFilters((prev) => ({
          ...prev,
          programCode: firstProgram.programCode,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programs]);

  // Auto-select the first transfer partner for Program Admin (Program filter)
  useEffect(() => {
    if (
      transferPartners.length > 0 &&
      !filters.transferPartnerId &&
      isProgramAdmin &&
      !loyyalAdmin
    ) {
      const firstPartner = transferPartners[0];
      if (firstPartner?.id) {
        setFilters((prev) => ({
          ...prev,
          transferPartnerId: String(firstPartner.id),
        }));
      }
    }
    // eslint-disable-next-line
  }, [transferPartners]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(0);
  };

  // Helper to get start and end in millis for a given range
  const getDateRangeMillis = (range: string) => {
    const now = new Date();
    let start, end;
    switch (range) {
      case "30days":
        start = new Date(now);
        start.setDate(now.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "7days":
        start = new Date(now);
        start.setDate(now.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "today":
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "yesterday":
        start = new Date(now);
        start.setDate(now.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setDate(now.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case "thisMonth":
        start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "lastMonth":
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      default:
        start = null;
        end = null;
    }
    return {
      startDateInMillis: start ? start.getTime() : "",
      endDateInMillis: end ? end.getTime() : "",
    };
  };

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    let newFilters;
    if (range === "none") {
      newFilters = {
        ...filters,
        startDateInMillis: "",
        endDateInMillis: "",
      };
    } else {
      const { startDateInMillis, endDateInMillis } = getDateRangeMillis(range);
      newFilters = {
        ...filters,
        startDateInMillis,
        endDateInMillis,
      };
    }
    setFilters(newFilters);
    setCurrentPage(0);
    // Fetch transactions with new date filters
    fetchTransactionsWithFilters({ ...newFilters, pageNumber: 0 });
  };

  const handleOtherFilterChange = (newFilters: any) => {
    setDateRange("none");
    const mergedFilters = {
      ...filters,
      ...newFilters,
    };
    setFilters(mergedFilters);
    setCurrentPage(0);
    // Fetch transactions with new filters
    fetchTransactionsWithFilters({ ...mergedFilters, pageNumber: 0 });
  };

  const handleExport = async (format: "csv" | "csv-all-programs" | "pdf") => {
    if (format === "csv" || format === "csv-all-programs") {
      try {
        const exportAllPrograms = format === "csv-all-programs";
        const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
        const searchId = debouncedSearchTerm.trim();
        const programId = exportAllPrograms ? null : resolveProgramId(filters);
        const payload = {
          ...filters,
          startDateInMillis: filters.startDateInMillis || "",
          endDateInMillis: filters.endDateInMillis || "",
          sortBy: sortBy.field || "transactionDate",
          sortOrder: (sortBy.direction as "asc" | "desc") || "desc",
          pageNumber: 0,
          pageSize: 10000,
          programType: "TRANSFER",
          programCode: exportAllPrograms ? "" : filters.programCode || "",
          ...(programId ? { programId } : {}),
          ...(searchId ? { id: searchId } : {}),
        };
        if (exportAllPrograms) {
          delete (payload as { programId?: unknown }).programId;
        }
        const response = await fetch(
          `${API_BASE}/transaction/getTransactionsFromDbInCSVFormat`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
          },
        );
        if (!response.ok) {
          throw new Error("Failed to export CSV");
        }
        const dateSuffix = new Date().toISOString().split("T")[0];
        const filename = await downloadResponseFile(
          response,
          exportAllPrograms
            ? `transactions_all_programs_${dateSuffix}.csv`
            : `transactions_${dateSuffix}.csv`,
        );
        toast.success(`Transactions exported as ${filename} successfully!`);
      } catch (error) {
        console.error("Error exporting transactions:", error);
        toast.error("Failed to export transactions as CSV");
      }
      return;
    }
    if (format === "pdf") {
      // Only export columns visible on screen
      const exportData = transactions.map((tx: any) => ({
        "Date & Time": tx.dateTime || "",
        "Member Id (From)": tx.transferPartnerMemberId || "",
        "Member Id (To)": tx.programMemberId || "",
        "Transaction Id": tx.transactionId || tx.id || "",
        "Partner Name": tx.partnerName || "",
        "Points Redeemed": tx.pointsRedeemed || "",
        "Redemption Amount": tx.redemptionAmount || "",
        "Points Earned": tx.pointsEarned || "",
        "Accrued Amount": `${tx.accruedAmount || ""} ${
          tx.accruedCurrency || ""
        }`.trim(),
        Program: tx.program || "",
        Status: tx.status || "",
      }));
      try {
        downloadTransactionsPDF(exportData as any);
        toast.success("Transactions exported as PDF successfully!");
      } catch (error) {
        console.error("Error exporting transactions:", error);
        toast.error("Failed to export transactions as PDF");
      }
    }
  };

  const handleReset = () => {
    setSearchTerm("");
    // Keep programCode selection (top dropdown) — only reset other filters
    const preservedProgramCode = filters.programCode || "";
    setFilters({
      startDateInMillis: "",
      endDateInMillis: "",
      ...(preservedProgramCode ? { programCode: preservedProgramCode } : {}),
    });
    setDateRange("30days");
    setSortBy({ field: "transactionDate", direction: "desc" });
    setCustomDateRange(undefined); // Reset the custom date range
    setResetKey((prev) => prev + 1); // Trigger UI reset in filters
    // Clear all advanced filter states by calling the filter change with empty values
    handleOtherFilterChange({
      transferStatuses: [],
      programCode: preservedProgramCode,
      transferPartnerId: "",
      transferPartnerTransactionId: "",
      startDateInMillis: "",
      endDateInMillis: "",
    });
    toast.success("All filters have been reset");
  };

  const handleSort = (field: string) => {
    if (sortBy.field === field) {
      setSortBy({
        field,
        direction: sortBy.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortBy({ field, direction: "asc" });
    }
  };

  const handleViewDetails = (transaction: TransferTransactionDisplay) => {
    setSelectedTransaction(transaction);
    setIsDetailsModalOpen(true);
  };

  const handleMarkRefunded = (transactionId: string) => {
    toast.success(`Transaction ${transactionId} marked as refunded`);
    // In a real app, we would update the transaction status
  };

  const SortableHeader = ({
    field,
    children,
  }: {
    field: string;
    children: React.ReactNode;
  }) => {
    const isSorted = sortBy.field === field;
    const direction = sortBy.direction;

    return (
      <div
        className="flex items-center cursor-pointer"
        onClick={() => handleSort(field)}
      >
        {children}
        <span className="ml-1">
          {isSorted && direction === "asc" && <ArrowUp className="h-4 w-4" />}
          {isSorted && direction === "desc" && (
            <ArrowDown className="h-4 w-4" />
          )}
          {!isSorted && <ChevronRight className="h-4 w-4 opacity-30" />}
        </span>
      </div>
    );
  };

  // Add a handler for custom date range
  const handleCustomDateRangeChange = (range: DateRange | undefined) => {
    setCustomDateRange(range);
    if (range && range.from && range.to) {
      // Set start date to beginning of day (00:00:00)
      const startDate = new Date(range.from);
      startDate.setHours(0, 0, 0, 0);
      const startDateInMillis = startDate.getTime();

      // Set end date to end of day (23:59:59)
      const endDate = new Date(range.to);
      endDate.setHours(23, 59, 59, 999);
      const endDateInMillis = endDate.getTime();

      const newFilters = {
        ...filters,
        startDateInMillis,
        endDateInMillis,
      };
      setFilters(newFilters);
      setCurrentPage(0);
      fetchTransactionsWithFilters({ ...newFilters, pageNumber: 0 });
      setDatePopoverOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Transactions</h1>
          <p className="text-gray-500 mt-1">
            View and filter all loyalty program transactions
          </p>
        </div>
        <div className="w-full sm:w-64 sm:ml-auto">
          <Select
            value={filters.programCode || ""}
            onValueChange={handleProgramChange}
            disabled={programs.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Program" />
            </SelectTrigger>
            <SelectContent>
              {programs.map((program: any) => (
                <SelectItem key={program.id} value={program.programCode}>
                  {program.programName || program.programCode}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Search and Date Range Picker Row */}

      {/* Transaction Stats */}
      <TransactionStats
        isLoading={isLoading}
        pointsTitle="Points Exchanged"
        pointsDescription="Total points Exchanged"
        stats={{
          totalTransactions: stats.totalNumberOfTransactions ?? 0,
          totalValue: (
            <>
              {stats.totalAmountOfTransactions?.toLocaleString("en-US", {
                maximumFractionDigits: 2,
              }) ?? "0.00"}{" "}
              <span className="text-sm font-normal">
                {stats.currencyVO?.iso3CurrencyCode || ""}
              </span>
            </>
          ),
          pointsEarned: stats.totalPointsRedeemed ?? 0,
          pointsBurned: stats.totalPointsPending ?? 0,
          avgPointsPerTransaction: stats.avgPointsPerTransaction,
        }}
      />

      {/* Transaction Filters */}
      <TransferTransactionFilters
        onSearch={handleSearch}
        onFilterChange={handleOtherFilterChange}
        onExport={handleExport}
        onDateRangeChange={handleDateRangeChange}
        onReset={handleReset}
        programs={availablePrograms}
        transferPartners={transferPartners}
        allPrograms={programs}
        searchTerm={searchTerm}
        resetKey={resetKey}
        initialTransferPartnerId={filters.transferPartnerId}
      />

      {/* Transactions Table */}
      {isLoading ? (
        <TransactionTableSkeleton rowCount={rowsPerPage} />
      ) : (
        <div className="w-full overflow-x-auto">
          <DataTable
            title="All Transactions"
            data={transactions}
            columns={[
              {
                header: "Txn Id",
                accessorKey: "transactionId",
                cell: (row) => row.transactionId,
                width: "140px",
              },
              {
                header: "Tranfer Txn Id",
                accessorKey: "transferPartnerTransactionId",
                cell: (row) => row.transferPartnerTransactionId || "",
                width: "140px",
              },
              {
                header: (
                  <SortableHeader field="transactionDate">Date</SortableHeader>
                ),
                accessorKey: "dateTime",
                cell: (row) => (
                  <span className="whitespace-nowrap">{row.dateTime}</span>
                ),
                width: "125px",
              },
              {
                header: "Program",
                accessorKey: "partnerName",
                cell: (row) => row.partnerName,
                width: "150px",
              },
              {
                header: "Member Id (From)",
                accessorKey: "transferPartnerMemberId",
                cell: (row) => row.transferPartnerMemberId,
                width: "120px",
              },
              {
                header: (
                  <SortableHeader field="transferPartnerPoints">
                    <div className="text-sm">
                      <div className="font-bold">Points Redeemed</div>
                      <div className="text-gray-500 text-xs">
                        Redemption Amount
                      </div>
                    </div>
                  </SortableHeader>
                ),
                id: "redemptionDetails",
                width: "160px",
                cell: (row) => (
                  <div className="text-sm">
                    <div className="font-medium">{row.pointsRedeemed} pts</div>
                    <div className="text-gray-500 text-xs">
                      {row.redemptionAmount} {row.redemptionCurrency}
                    </div>
                  </div>
                ),
              },
              {
                header: (
                  <SortableHeader field="programPoints">
                    <div className="text-sm">
                      <div className="font-bold">Points Earned</div>
                      <div className="text-gray-500 text-xs">
                        Accrued Amount
                      </div>
                    </div>
                  </SortableHeader>
                ),
                id: "earnedDetails",
                width: "160px",
                cell: (row) => (
                  <div className="text-sm">
                    <div className="font-medium">{row.pointsEarned} pts</div>
                    <div className="text-gray-500 text-xs">
                      {row.accruedAmount} {row.accruedCurrency}
                    </div>
                  </div>
                ),
              },
              {
                header: "Partner Name",
                accessorKey: "program",
                cell: (row) => row.program,
                width: "150px",
              },
              {
                header: "Member Id (To)",
                accessorKey: "programMemberId",
                cell: (row) => row.programMemberId,
                width: "120px",
              },
              {
                header: (
                  <SortableHeader field="transferStatus">Status</SortableHeader>
                ),
                accessorKey: "status",
                width: "130px",
                cell: (row) => {
                  const statusStyles = {
                    accrual_success:
                      "bg-green-100 text-green-800 hover:bg-green-100",
                    redemption_success:
                      "bg-blue-100 text-blue-800 hover:bg-blue-100",
                    pending:
                      "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
                    failed: "bg-red-100 text-red-800 hover:bg-red-100",
                  };

                  const statusLabels = {
                    accrual_success: "Accrual Success",
                    redemption_success: "Redemption Success",
                    pending: "Pending",
                    failed: "Accrual Failed",
                  };

                  const statusKey = row.status
                    .replace(/\s/g, "_")
                    .toLowerCase();
                  return (
                    <Badge
                      variant="outline"
                      className={statusStyles[statusKey] || ""}
                    >
                      {/* {row.status} */}
                      {statusLabels[statusKey] || row.status}
                    </Badge>
                  );
                },
              },
              {
                header: "Action",
                id: "actions",
                width: "80px",
                cell: (row) => (
                  <div className="flex justify-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem
                          onClick={() => handleViewDetails(row)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          <span>View Details</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        open={isDetailsModalOpen}
        onOpenChange={setIsDetailsModalOpen}
        item={selectedTransaction}
        originalItem={selectedTransaction?._original}
      />

      {/* Pagination controls */}
      {!isLoading && totalPages > 0 && (
        <div className="mt-4 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Rows per page:</span>
              <Select
                value={rowsPerPage.toString()}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder={rowsPerPage.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <button
                      className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                        currentPage === 0
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => setCurrentPage(0)}
                      disabled={currentPage === 0}
                      aria-label="First page"
                      type="button"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </button>
                  </PaginationItem>
                  {currentPage > 0 ? (
                    <PaginationPrevious
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(0, prev - 1))
                      }
                      className="gap-0 px-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </PaginationPrevious>
                  ) : (
                    <PaginationPrevious
                      onClick={() => {}}
                      className="pointer-events-none opacity-50 gap-0 px-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </PaginationPrevious>
                  )}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNumber =
                      currentPage <= 2 ? i : currentPage + i - 2;
                    if (pageNumber >= totalPages) return null;
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          onClick={() => setCurrentPage(pageNumber)}
                          isActive={currentPage === pageNumber}
                        >
                          {pageNumber + 1}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  {currentPage < totalPages - 1 ? (
                    <PaginationNext
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(totalPages - 1, prev + 1),
                        )
                      }
                      className="gap-0 px-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </PaginationNext>
                  ) : (
                    <PaginationNext
                      onClick={() => {}}
                      className="pointer-events-none opacity-50 gap-0 px-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </PaginationNext>
                  )}
                  <PaginationItem>
                    <button
                      className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                        currentPage === totalPages - 1
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => setCurrentPage(totalPages - 1)}
                      disabled={currentPage === totalPages - 1}
                      aria-label="Last page"
                      type="button"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </button>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
            <div className="w-[160px]" /> {/* Spacer to balance layout */}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferTransactions;
